/*
 * Progressive disclosure for the product form's gift options.
 *
 * A field that should not reach the cart has its `name` attribute removed, not
 * just `disabled` set. The dynamic checkout button (今すぐ購入) serializes the
 * form without honouring `disabled`, so a disabled-but-named field still shows
 * up as a line-item property on the order. Nameless fields are invisible to
 * every serializer. `disabled` is kept as well, to take the hidden controls out
 * of the tab order.
 *
 * A field is named only when its panel is open, and — for free-text fields —
 * only when the customer actually typed something.
 */
if (!customElements.get('gift-options')) {
  class GiftOptions extends HTMLElement {
    connectedCallback() {
      this.wrappingToggle = this.querySelector('[data-wrapping-toggle]');
      this.wrappingPanel = this.querySelector('[data-wrapping-panel]');
      this.wrappingKinds = Array.from(this.querySelectorAll('[data-wrapping-kind]'));
      this.noshiPanel = this.querySelector('[data-noshi-panel]');
      this.ribbonPanel = this.querySelector('[data-ribbon-panel]');
      this.messageToggle = this.querySelector('[data-message-toggle]');
      this.messagePanel = this.querySelector('[data-message-panel]');

      // Must run before any sync strips a name, or the original is lost.
      this.querySelectorAll('[name]').forEach((field) => {
        field.dataset.propertyName = field.getAttribute('name');
      });

      this.querySelectorAll('[data-optional-text]').forEach((field) => {
        field.addEventListener('input', () => this.applyFieldState(field, !field.disabled));
      });

      if (this.wrappingToggle && this.wrappingPanel) {
        this.wrappingToggle.addEventListener('change', () => this.syncWrapping());
        this.wrappingKinds.forEach((radio) => {
          radio.addEventListener('change', () => this.syncWrappingKind());
        });
      }

      if (this.messageToggle && this.messagePanel) {
        this.messageToggle.addEventListener('change', () => this.syncMessageCard());
      }

      this.syncAll();

      // Returning via the back button restores the checkbox and radio states
      // without firing `change`, leaving the toggles on but their panels shut.
      // pageshow runs after that restoration, on both bfcache and normal loads.
      this.handlePageShow = () => this.syncAll();
      window.addEventListener('pageshow', this.handlePageShow);
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.handlePageShow);
    }

    syncAll() {
      if (this.wrappingToggle && this.wrappingPanel) this.syncWrapping();
      if (this.messageToggle && this.messagePanel) this.syncMessageCard();
    }

    // The stable key from data-wrapping-kind, not the radio's value — the value
    // is customer-facing text and gets reworded.
    get selectedKind() {
      const checked = this.wrappingKinds.find((radio) => radio.checked);
      return checked ? checked.dataset.wrappingKind : null;
    }

    applyFieldState(field, active) {
      field.disabled = !active;

      const isOptionalText = field.hasAttribute('data-optional-text');
      const shouldSubmit = active && (!isOptionalText || field.value.trim() !== '');

      if (shouldSubmit) {
        field.setAttribute('name', field.dataset.propertyName);
      } else {
        field.removeAttribute('name');
      }
    }

    setPanelActive(panel, active) {
      if (!panel) return;

      panel.hidden = !active;
      panel.querySelectorAll('input, select, textarea').forEach((field) => {
        this.applyFieldState(field, active);
      });
    }

    syncWrapping() {
      const active = this.wrappingToggle.checked;

      // Activates every descendant, including the sub-panels; syncWrappingKind
      // immediately deactivates whichever ones are not selected.
      this.setPanelActive(this.wrappingPanel, active);

      if (active) {
        this.syncWrappingKind();
      } else {
        this.setPanelActive(this.noshiPanel, false);
        this.setPanelActive(this.ribbonPanel, false);
      }
    }

    // 包装紙の色 applies to every kind, so it needs no branch here — the wrapping
    // panel already reveals and enables it.
    syncWrappingKind() {
      const kind = this.selectedKind;
      this.setPanelActive(this.noshiPanel, kind === 'noshi');
      this.setPanelActive(this.ribbonPanel, kind === 'ribbon');
    }

    syncMessageCard() {
      this.setPanelActive(this.messagePanel, this.messageToggle.checked);
    }
  }

  customElements.define('gift-options', GiftOptions);
}
